<template>
	<div v-if="!showPopUp && images?.length" class="relative flex">
		<ArrowImageGallery
			v-if="showArrows.top"
			arrow-direction="left"
			:number-of-slides="images?.length"
			class="hidden md:flex"
			@click="slideTop"
		/>
		<div
			ref="imageListRef"
			class="flex w-full max-w-full md:justify-start justify-center gap-2 overflow-x-auto no-scrollbar"
		>
			<div
				v-for="(image, index) in images"
				:key="imageId(image) + index"
				class="cursor-pointer md:w-20 md:min-w-20 md:min-h-20 overflow-hidden"
			>
				<button
					type="button"
					class="w-3 h-3 md:w-auto md:h-auto rounded-full md:rounded-none bg-black md:bg-transparent opacity-25 md:opacity-100"
					:class="bigImageIndex === index ? '!opacity-100' : ''"
					@click="setBigImageIndex(index)"
				>
					<NuxtImg
						:src="`${imagePath(image)}?format=webp&w=80`"
						:alt="$t('Thumbnail for image №{value}', { value: index + 1 })"
						loading="lazy"
						:class="[
							`hidden md:block w-full h-20 border-2 object-cover ${
								bigImageIndex === index
									? ' border-gray-super-dark '
									: ' border-white '
							}`
						]"
						:placeholder="
							img(`${imagePath(image)}?format=webp&w=80`, {
								h: 10,
								f: 'webp',
								blur: 2,
								q: 50
							})
						"
					/>
					<span class="sr-only">
						{{ $t("Go to image {value}", { value: index + 1 }) }}
					</span>
				</button>
			</div>
		</div>
		<ArrowImageGallery
			v-if="showArrows.bottom"
			arrow-direction="right"
			:number-of-slides="images?.length"
			class="hidden md:flex"
			@click="slideBottom"
		/>
	</div>
</template>

<script setup lang="ts">
import { onMounted, ref, reactive } from "vue";
import type { ProductFile } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { CategoryFiles } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { FilteredProductItemFiles } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type { BigImageIndex } from "~/types/storyblok/imageGallery/imageGallery";

interface Props {
	images: ProductFile[] | CategoryFiles[] | FilteredProductItemFiles[];
	bigImageIndex: number;
	setBigImageIndex: BigImageIndex;
	showPopUp: boolean;
}

const props = defineProps<Props>();

const img = useImage();

const imageListRef = ref<HTMLElement>();
const showArrows = reactive({
	top: true,
	bottom: true
});

const slideTop = () => {
	const stepSize = 80;
	if (imageListRef.value) {
		imageListRef.value.classList.add("scroll-smooth");
		const scrollLeft = imageListRef.value.scrollLeft - stepSize;
		imageListRef.value.scrollLeft = scrollLeft;
	}
};
const slideBottom = () => {
	const stepSize = 80;
	if (imageListRef.value) {
		imageListRef.value.classList.add("scroll-smooth");
		const scrollLeft = imageListRef.value.scrollLeft + stepSize;
		imageListRef.value.scrollLeft = scrollLeft;
	}
};

const slideToImage = ({
	displayedBigImage,
	imageListRef
}: {
	displayedBigImage: number;
	imageListRef?: Ref;
}) => {
	if (imageListRef?.value) {
		const stepSize = 80;
		imageListRef.value.classList.add("scroll-smooth");
		const scrollLeft = stepSize * displayedBigImage;
		imageListRef.value.scrollLeft = scrollLeft;
		showArrows.left = scrollLeft > 0;
	}
};

onMounted(() => {
	slideToImage({ displayedBigImage: props.bigImageIndex });
});

watchEffect(() => {
	slideToImage({ displayedBigImage: props.bigImageIndex, imageListRef });
});

const imagePath = (
	image: ProductFile | CategoryFiles | FilteredProductItemFiles
) => {
	return isCategoryFile(image) ? image?.ImagePath : image?.Path;
};

const imageId = (
	image: ProductFile | CategoryFiles | FilteredProductItemFiles
) => {
	return isCategoryFile(image) || isFilteredProductItemFiles(image)
		? image.ImageKey!
		: image.Key!;
};
</script>
